import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotificationContext } from '../contexts/NotificationContext';
import SchedulePreview from './SchedulePreview';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const AIScheduleGenerator = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [departments, setDepartments] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  
  const [params, setParams] = useState({
    department: '',
    academicYear: '',
    semester: 1,
    selectedClasses: [],
    selectedCourses: [],
    selectedTeachers: []
  });

  useEffect(() => {
    fetchInitialData();
    fetchDrafts();
  }, []);

  useEffect(() => {
    if (params.department && params.academicYear) {
      fetchClassesByDepartment();
      fetchCoursesByDepartment();
      fetchTeachersByDepartment();
    }
  }, [params.department, params.academicYear]);

  const fetchInitialData = async () => {
    try {
      const [deptRes, yearRes] = await Promise.all([
        axios.get(`${API_URL}/api/departments`),
        axios.get(`${API_URL}/api/academic-years`)
      ]);
      setDepartments(deptRes.data);
      setAcademicYears(yearRes.data);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };

  const fetchClassesByDepartment = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/classes`);
      const filtered = response.data.filter(cls => 
        cls.department._id === params.department &&
        cls.academicYear._id === params.academicYear
      );
      setClasses(filtered);
    } catch (error) {
      console.error('Erreur chargement classes:', error);
    }
  };

  const fetchCoursesByDepartment = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/courses?department=${params.department}&semester=${params.semester}`);
      setCourses(response.data);
    } catch (error) {
      console.error('Erreur chargement cours:', error);
    }
  };

  const fetchTeachersByDepartment = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/teachers`);
      const filtered = response.data.filter(teacher => 
        teacher.departments?.some(dept => dept._id === params.department)
      );
      setTeachers(filtered);
    } catch (error) {
      console.error('Erreur chargement enseignants:', error);
    }
  };

  const fetchDrafts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ai-schedule/drafts`);
      setDrafts(response.data);
    } catch (error) {
      console.error('Erreur chargement brouillons:', error);
    }
  };

  const handleGenerate = async () => {
    if (!params.department || !params.academicYear || params.selectedClasses.length === 0) {
      showError('Veuillez sélectionner au moins un département, une année et des classes');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/ai-schedule/generate`, {
        department: params.department,
        academicYear: params.academicYear,
        semester: params.semester,
        classes: params.selectedClasses,
        courses: params.selectedCourses.length > 0 ? params.selectedCourses : courses.map(c => c._id),
        teachers: params.selectedTeachers.length > 0 ? params.selectedTeachers : teachers.map(t => t._id)
      });

      showSuccess('Emploi du temps généré avec succès');
      setSelectedDraft(response.data.draft);
      setShowPreview(true);
      fetchDrafts();
    } catch (error) {
      console.error('Erreur génération:', error);
      showError('Erreur lors de la génération de l\'emploi du temps');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (draftId) => {
    try {
      const response = await axios.post(`${API_URL}/api/ai-schedule/validate/${draftId}`);
      showSuccess('Validation terminée');
      
      // Mettre à jour le brouillon avec les résultats de validation
      const updatedDraft = { ...selectedDraft, validationResults: response.data.validation };
      setSelectedDraft(updatedDraft);
      fetchDrafts();
    } catch (error) {
      console.error('Erreur validation:', error);
      showError('Erreur lors de la validation');
    }
  };

  const handleApprove = async (draftId) => {
    try {
      await axios.post(`${API_URL}/api/ai-schedule/approve/${draftId}`);
      showSuccess('Emploi du temps approuvé');
      fetchDrafts();
    } catch (error) {
      console.error('Erreur approbation:', error);
      showError('Erreur lors de l\'approbation');
    }
  };

  const handleApply = async (draftId) => {
    try {
      const response = await axios.post(`${API_URL}/api/ai-schedule/apply/${draftId}`);
      showSuccess(`Emploi du temps appliqué: ${response.data.result.sessionsCreated} séances créées`);
      fetchDrafts();
      setShowPreview(false);
    } catch (error) {
      console.error('Erreur application:', error);
      showError('Erreur lors de l\'application de l\'emploi du temps');
    }
  };

  const handlePreviewDraft = async (draftId) => {
    try {
      const response = await axios.get(`${API_URL}/api/ai-schedule/drafts/${draftId}`);
      setSelectedDraft(response.data);
      setShowPreview(true);
    } catch (error) {
      console.error('Erreur chargement brouillon:', error);
      showError('Erreur lors du chargement du brouillon');
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce brouillon ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/ai-schedule/drafts/${draftId}`);
      showSuccess('Brouillon supprimé');
      fetchDrafts();
      if (selectedDraft?._id === draftId) {
        setShowPreview(false);
        setSelectedDraft(null);
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      showError('Erreur lors de la suppression');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800';
      case 'PREVIEW': return 'bg-blue-100 text-blue-800';
      case 'APPROVED': return 'bg-green-100 text-green-800';
      case 'PUBLISHED': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'DRAFT': return 'Brouillon';
      case 'PREVIEW': return 'En validation';
      case 'APPROVED': return 'Approuvé';
      case 'PUBLISHED': return 'Publié';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">🤖 Génération IA d'Emplois du Temps</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Panneau de configuration */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Configuration</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
                  <select
                    value={params.department}
                    onChange={(e) => setParams({...params, department: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Sélectionner</option>
                    {departments.map(dept => (
                      <option key={dept._id} value={dept._id}>{dept.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Année universitaire</label>
                  <select
                    value={params.academicYear}
                    onChange={(e) => setParams({...params, academicYear: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Sélectionner</option>
                    {academicYears.map(year => (
                      <option key={year._id} value={year._id}>{year.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Semestre</label>
                  <select
                    value={params.semester}
                    onChange={(e) => setParams({...params, semester: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value={1}>Semestre 1</option>
                    <option value={2}>Semestre 2</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Classes ({classes.length})</label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                    {classes.map(cls => (
                      <label key={cls._id} className="flex items-center text-sm py-1">
                        <input
                          type="checkbox"
                          checked={params.selectedClasses.includes(cls._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setParams({...params, selectedClasses: [...params.selectedClasses, cls._id]});
                            } else {
                              setParams({...params, selectedClasses: params.selectedClasses.filter(id => id !== cls._id)});
                            }
                          }}
                          className="mr-2"
                        />
                        {cls.name}
                      </label>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleGenerate}
                  disabled={loading || !params.department || !params.academicYear || params.selectedClasses.length === 0}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? '🔄 Génération...' : '🚀 Générer avec IA'}
                </button>
              </div>
            </div>
          </div>

          {/* Liste des brouillons */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Brouillons d'emplois du temps</h2>
              
              {drafts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📋</div>
                  <p>Aucun brouillon disponible</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {drafts.map(draft => (
                    <div key={draft._id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium">{draft.name}</h3>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(draft.status)}`}>
                              {getStatusLabel(draft.status)}
                            </span>
                            {draft.aiScore && (
                              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                Score: {Math.round(draft.aiScore)}%
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {draft.department?.name} • {draft.academicYear?.name} • S{draft.semester}
                          </p>
                          <p className="text-xs text-gray-500">
                            {draft.sessions?.length || 0} séances • {new Date(draft.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => handlePreviewDraft(draft._id)}
                            className="px-3 py-1 text-xs font-medium text-primary-700 bg-primary-50 rounded hover:bg-primary-100"
                          >
                            👁️ Voir
                          </button>
                          
                          {draft.status === 'DRAFT' && (
                            <button
                              onClick={() => handleValidate(draft._id)}
                              className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100"
                            >
                              ✅ Valider
                            </button>
                          )}
                          
                          {draft.status === 'PREVIEW' && (
                            <button
                              onClick={() => handleApprove(draft._id)}
                              className="px-3 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100"
                            >
                              👍 Approuver
                            </button>
                          )}
                          
                          {draft.status === 'APPROVED' && (
                            <button
                              onClick={() => handleApply(draft._id)}
                              className="px-3 py-1 text-xs font-medium text-purple-700 bg-purple-50 rounded hover:bg-purple-100"
                            >
                              🚀 Appliquer
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleDeleteDraft(draft._id)}
                            className="px-3 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      
                      {draft.validationResults && (
                        <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
                          <div className="flex gap-4">
                            <span className="text-green-600">✅ Contraintes: {draft.validationResults.score?.hardConstraints || 0}%</span>
                            <span className="text-orange-600">⚠️ Conflits: {(draft.validationResults.teacherConflicts?.length || 0) + (draft.validationResults.roomConflicts?.length || 0)}</span>
                            <span className="text-blue-600">📊 Score total: {Math.round(draft.validationResults.score?.total || 0)}%</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal de prévisualisation */}
        {showPreview && selectedDraft && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium">Prévisualisation: {selectedDraft.name}</h3>
                <button 
                  onClick={() => setShowPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="p-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-50 p-3 rounded">
                    <div className="text-sm text-blue-600">Score IA</div>
                    <div className="text-2xl font-bold text-blue-800">{Math.round(selectedDraft.aiScore || 0)}%</div>
                  </div>
                  <div className="bg-green-50 p-3 rounded">
                    <div className="text-sm text-green-600">Séances</div>
                    <div className="text-2xl font-bold text-green-800">{selectedDraft.sessions?.length || 0}</div>
                  </div>
                </div>
                
                {selectedDraft.validationResults && (
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <h4 className="font-medium mb-2">Résultats de validation</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Contraintes dures:</span>
                        <span className="ml-2 font-medium">{selectedDraft.validationResults.score?.hardConstraints || 0}%</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Conflits enseignants:</span>
                        <span className="ml-2 font-medium">{selectedDraft.validationResults.teacherConflicts?.length || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Conflits salles:</span>
                        <span className="ml-2 font-medium">{selectedDraft.validationResults.roomConflicts?.length || 0}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <SchedulePreview
                  draft={selectedDraft}
                  onClose={() => setShowPreview(false)}
                  onApprove={() => handleApprove(selectedDraft._id)}
                  onReject={() => {
                    setShowPreview(false);
                    setSelectedDraft(null);
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIScheduleGenerator;